// packages/core/echo/model-factory/src/model.ts
var Model = class {
  /**
  * @param _meta Metadata definitions.
  * @param _itemId Parent item.
  * @param _getState Retrieves the underlying state object.
  * @param _mutationWriter Output mutation stream (unless read-only).
  */
  constructor(_meta, _itemId, _getState, _mutationWriter) {
    this._meta = _meta;
    this._itemId = _itemId;
    this._getState = _getState;
    this._mutationWriter = _mutationWriter;
  }
  toString() {
    return `Model(${JSON.stringify(this.toJSON())})`;
  }
  toJSON() {
    return {
      id: this.itemId,
      type: this._meta.type
    };
  }
  get modelMeta() {
    return this._meta;
  }
  get itemId() {
    return this._itemId;
  }
  get readOnly() {
    return this._mutationWriter === void 0;
  }
  /**
  * Writes the raw mutation to the output stream.
  */
  async write(mutation) {
    if (!this._mutationWriter) {
      throw new Error(`Read-only model: ${this._itemId}`);
    }
    return this._mutationWriter(mutation);
  }
};

// packages/core/echo/model-factory/src/model-factory.ts
import assert2 from "@dxos/node-std/assert";
import { Event, scheduleTask } from "@dxos/async";
import { Context } from "@dxos/context";
import { todo } from "@dxos/debug";

// packages/core/echo/model-factory/src/types.ts
import assert from "@dxos/node-std/assert";
function validateModelClass(model) {
  assert(typeof model === "function");
  if (!model.meta) {
    throw new TypeError("Invalid model: missing static `meta` field.");
  }
  if (!model.meta.type) {
    throw new TypeError("Invalid model: missing type URL.");
  }
  if (!model.meta.mutationCodec) {
    throw new TypeError("Invalid model: missing mutation codec.");
  }
}

// packages/core/echo/model-factory/src/model-factory.ts
var ModelFactory = class {
  constructor() {
    this.registered = new Event();
    this._models = /* @__PURE__ */ new Map();
  }
  hasModel(modelType) {
    return this.getModel(modelType) !== void 0;
  }
  getModels() {
    return Array.from(this._models.values());
  }
  getModel(modelType) {
    assert2(modelType);
    return this._models.get(modelType);
  }
  // TODO(burdon): Remove?
  getModelMeta(modelType) {
    if (!this._models.has(modelType)) {
      throw new Error(`Invalid model type: ${modelType}`);
    }
    const { meta } = this._models.get(modelType);
    return meta;
  }
  // TODO(burdon): Test if already registered.
  registerModel(constructor) {
    validateModelClass(constructor);
    const { meta } = constructor;
    this._models.set(meta.type, {
      meta,
      constructor
    });
    scheduleTask(new Context(), () => {
      this.registered.emit(constructor);
    });
    return this;
  }
  /**
  * Instantiates new StateManager with the underlying model.
  * @param modelType Model type DXN.
  * @param itemId Id of the item holding the model.
  * @param snapshot Snapshot defining the intial state. `{}` can be provided for empty state.
  * @param memberKey IDENTITY key of the member authoring the model's mutations.
  * @param writeStream Stream for outbound messages.
  * @deprecated
  */
  createModel(modelType, itemId, _snapshot, _memberKey, _writeStream) {
    var _a;
    assert2(itemId);
    const _constructor = (_a = this._models.get(modelType)) == null ? void 0 : _a.constructor;
    return todo();
  }
};

// packages/core/echo/model-factory/src/testing/messages.ts
import { schema } from "@dxos/protocols";
import { Timeframe } from "@dxos/timeframe";
var createSetPropertyMutation = (objectId, key, value, timeframe = new Timeframe()) => ({
  timeframe,
  payload: {
    data: {
      batch: {
        objects: [
          {
            objectId,
            mutations: [
              {
                model: {
                  "@type": "google.protobuf.Any",
                  typeUrl: "todo",
                  value: schema.getCodecForType("example.testing.data.TestItemMutation").encode({
                    key,
                    value
                  })
                }
              }
            ]
          }
        ]
      }
    }
  }
});

// packages/core/echo/model-factory/src/testing/test-builder.ts
import debug from "debug";
import { Trigger } from "@dxos/async";
import { createFeedWriter } from "@dxos/feed-store";
import { PublicKey } from "@dxos/keys";
import { Timeframe as Timeframe2 } from "@dxos/timeframe";
import { ComplexMap } from "@dxos/util";
var log = debug("dxos:echo:model-test-rig");
var TestBuilder = class {
  // prettier-ignore
  constructor(_modelFactory, _modelConstructor) {
    this._modelFactory = _modelFactory;
    this._modelConstructor = _modelConstructor;
    this._peers = new ComplexMap(PublicKey.hash);
    this._replicationFinished = new Trigger();
    this._replicating = true;
    this._replicationFinished.wake();
  }
  createPeer() {
    const key = PublicKey.random();
    const writer = createFeedWriter((data) => {
      return Promise.resolve(this._writeMessage(key, data));
    });
    const id = PublicKey.random().toHex();
    const stateManager = this._modelFactory.createModel(this._modelConstructor.meta.type, id, {
      objectId: "test"
    }, key, writer);
    const peer = new TestPeer(stateManager, key);
    this._peers.set(key, peer);
    return peer;
  }
  configureReplication(value) {
    this._replicating = value;
    this._replicate();
  }
  async waitForReplication() {
    log("Waiting for replication...");
    await this._replicationFinished.wait();
    log("Replications started.");
  }
  _writeMessage(peerKey, mutation) {
    const peer = this._peers.get(peerKey);
    const seq = peer.mutations.length;
    const timeframe = peer.timeframe;
    log(`Write ${peerKey}:${seq}`);
    const message = {
      meta: {
        feedKey: peerKey,
        memberKey: peerKey,
        seq,
        timeframe
      },
      mutation
    };
    peer.mutations.push(message);
    setTimeout(() => peer.processMutation(message));
    if (this._replicating) {
      setTimeout(() => this._replicate());
    }
    this._replicationFinished.reset();
    log("Reset replication lock.");
    return {
      feedKey: peerKey,
      seq
    };
  }
  _replicate() {
    for (const peer of this._peers.values()) {
      for (const [feed, { mutations }] of this._peers) {
        if (peer.key.equals(feed)) {
          continue;
        }
        const timeframeSeq = peer.timeframe.get(feed);
        const startingIndex = timeframeSeq === void 0 ? 0 : timeframeSeq + 1;
        log(`Replicating feed ${feed} -> ${peer.key} range [${startingIndex}; ${mutations.length})`);
        for (let i = startingIndex; i < mutations.length; i++) {
          log(`Process ${feed}:${i} -> ${peer.key}`);
          peer.processMutation(mutations[i]);
        }
      }
    }
    this._replicationFinished.wake();
    log("Wake replication lock.");
  }
};
var TestPeer = class {
  // prettier-ignore
  constructor(stateManager, key) {
    this.stateManager = stateManager;
    this.key = key;
    this.timeframe = new Timeframe2();
    this.mutations = [];
  }
  get model() {
    return null;
  }
  processMutation(message) {
    this.stateManager.processMessage(message.meta, message.mutation);
    this.timeframe = Timeframe2.merge(this.timeframe, new Timeframe2([
      [
        PublicKey.from(message.meta.feedKey),
        message.meta.seq
      ]
    ]));
  }
};

// packages/core/echo/model-factory/src/testing/test-model.ts
import { checkType } from "@dxos/debug";
import { schema as schema2 } from "@dxos/protocols";
var TestModelStateMachine = class {
  constructor() {
    this._state = /* @__PURE__ */ new Map();
  }
  getState() {
    return this._state;
  }
  process(mutation) {
    const { key, value } = mutation;
    this._state.set(key, value);
  }
  snapshot() {
    return {
      keys: Array.from(this._state.entries()).map(([key, value]) => ({
        key,
        value
      }))
    };
  }
  reset(snapshot) {
    var _a;
    this._state.clear();
    ((_a = snapshot.keys) != null ? _a : []).forEach(({ key, value }) => this._state.set(key, value));
  }
};
var TestModel = class extends Model {
  get keys() {
    return Array.from(this._getState().keys());
  }
  get properties() {
    return Object.fromEntries(this._getState());
  }
  get(key) {
    return this._getState().get(key);
  }
  async set(key, value) {
    const receipt = await this.write(checkType({
      key,
      value
    }));
    await receipt.waitToBeProcessed();
  }
};
TestModel.meta = {
  type: "dxos:model/test",
  stateMachine: () => new TestModelStateMachine(),
  mutationCodec: schema2.getCodecForType("example.testing.data.TestItemMutation"),
  snapshotCodec: schema2.getCodecForType("example.testing.data.TestItemSnapshot")
};

// packages/core/echo/model-factory/src/testing/test-list-model.ts
import { schema as schema3 } from "@dxos/protocols";
var TestListModelStateMachine = class {
  constructor() {
    this._messages = [];
  }
  getState() {
    return this._messages;
  }
  process(mutation) {
    this._messages.push(mutation);
  }
  snapshot() {
    throw new Error("Method not implemented.");
  }
  reset(snapshot) {
    throw new Error("Method not implemented.");
  }
};
var TestListModel = class extends Model {
  get messages() {
    return this._getState();
  }
  async sendMessage(data) {
    const receipt = await this.write({
      data
    });
    await receipt.waitToBeProcessed();
  }
};
TestListModel.meta = {
  type: "dxos:model/test-list",
  stateMachine: () => new TestListModelStateMachine(),
  mutationCodec: schema3.getCodecForType("example.testing.data.TestListMutation")
};
export {
  Model,
  ModelFactory,
  TestBuilder,
  TestListModel,
  TestModel,
  TestPeer,
  createSetPropertyMutation,
  validateModelClass
};
//# sourceMappingURL=index.mjs.map
